import React, { useState, useEffect } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Globe, Image as ImageIcon, User, Palette, Link as LinkIcon,
  Save, RefreshCw, Eye, EyeOff, Settings, Upload, Trash2, Plus, ChevronDown,
  ChevronUp, Instagram, Twitter, Mail, MessageCircle
} from "lucide-react";

const DEFAULT_LINK = () => ({
  key: `link_${Math.random().toString(36).slice(2, 8)}`,
  label: "",
  sub: "",
  url: "",
  accent: "#14B8A6",
  icon: "🔗",
  thumb_url: "",
  enabled: true,
});

const TABS = {
  BASICS: "basics",
  PROFILE: "profile",
  GALLERY: "gallery",
  LINKS: "links",
  SOCIALS: "socials",
  THEME: "theme",
};

const THEMES = [
  { value: "dark_lux", label: "Dark Luxury", description: "Noir elegance with gold accents" },
  { value: "neon_sin", label: "Neon Sin", description: "Cyberpunk pink and purple" },
  { value: "bordello", label: "Bordello", description: "Velvet reds and rich textures" },
  { value: "soft_sensual", label: "Soft Sensual", description: "Cream and rose gold" },
  { value: "greek_cyber", label: "Greek Cyber", description: "Blue and white with neon" },
];

const HERO_SIZES = [
  { value: "compact", label: "Compact", description: "~52vh height" },
  { value: "medium", label: "Medium", description: "~65vh height" },
  { value: "full", label: "Full", description: "~78vh height" },
];

export default function LandingEditorNew() {
  const [activeTab, setActiveTab] = useState(TABS.BASICS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    name: "",
    tagline: "",
    bio: "",
    avatar_url: "",
    cover_url: "",
    about: "",
    theme: "dark_lux",
    font_heading: "Cormorant Garamond",
    font_body: "Inter",
    hero_size: "compact",
    show_message_form: true,
    show_model_apply: true,
    message_intro: "",
    profile_attrs: {
      age: "",
      ethnicity: "",
      height: "",
      weight: "",
      cock_size: "",
      foreskin: "",
      style: "",
      position: "",
      body_type: "",
      body_hair: "",
      orientation: "",
      zodiac: "",
      eye_color: "",
      hair_color: "",
      piercings: "",
      smoking: "",
      tattoos: "",
      safe_bb: "",
      health_status: "",
      hiv_status: "",
    },
    gallery_photos: [],
    links: [],
    socials: [],
    free_area_title: "Welcome to the Collection",
    free_area_description: "Enjoy exclusive trailers, behind-the-scenes content, and studio highlights. Upgrade to VIP for full uncut videos and premium access.",
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/landing/config");
      setConfig((previous) => ({
        ...previous, ...res.data,
        profile_attrs: res.data?.profile_attrs || {},
        gallery_photos: Array.isArray(res.data?.gallery_photos) ? res.data.gallery_photos : [],
        links: Array.isArray(res.data?.links)
          ? res.data.links.map((link, index) => ({ ...DEFAULT_LINK(), ...link, order: link.order ?? index }))
          : [],
        socials: Array.isArray(res.data?.socials) ? res.data.socials : [],
      }));
    } catch (e) {
      toast.error("Failed to load landing config");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        ...config,
        links: config.links.map((link, index) => ({ ...link, order: index })),
      };
      await api.put("/api/landing/config", payload);
      toast.success("Landing page updated!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const updateLink = (index, patch) => setConfig((prev) => ({
    ...prev,
    links: prev.links.map((link, i) => i === index ? { ...link, ...patch } : link),
  }));

  const addLink = () => setConfig((prev) => ({
    ...prev,
    links: [...prev.links, { ...DEFAULT_LINK(), order: prev.links.length }],
  }));

  const removeLink = (index) => setConfig((prev) => ({
    ...prev,
    links: prev.links.filter((_, i) => i !== index),
  }));

  const moveLink = (index, direction) => setConfig((prev) => {
    const links = [...prev.links];
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= links.length) return prev;
    [links[index], links[nextIndex]] = [links[nextIndex], links[index]];
    return { ...prev, links };
  });

  const updateProfileAttr = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      profile_attrs: { ...prev.profile_attrs, [field]: value },
    }));
  };

  const addGalleryPhoto = () => {
    const url = prompt("Enter photo URL (from Cloudinary or other CDN):");
    if (url) {
      setConfig((prev) => ({
        ...prev,
        gallery_photos: [...prev.gallery_photos, { url, caption: "", order: prev.gallery_photos.length }],
      }));
    }
  };

  const removeGalleryPhoto = (index) => {
    setConfig((prev) => ({
      ...prev,
      gallery_photos: prev.gallery_photos.filter((_, i) => i !== index),
    }));
  };

  const addSocial = () => {
    const key = prompt("Platform (twitter, instagram, email, telegram):");
    const url = prompt("URL:");
    if (key && url) {
      setConfig((prev) => ({
        ...prev,
        socials: [...prev.socials, { key, url, enabled: true }],
      }));
    }
  };

  const removeSocial = (index) => {
    setConfig((prev) => ({
      ...prev,
      socials: prev.socials.filter((_, i) => i !== index),
    }));
  };

  if (loading) {
    return (
      <AppLayout title="Landing Editor">
        <div className="text-center py-12">
          <RefreshCw size={32} className="animate-spin mx-auto mb-4" style={{ color: "var(--gio-muted)" }} />
          <p style={{ color: "var(--gio-muted)" }}>Loading...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title="Landing Page Editor"
      overline="GIOPILOS.COM · PUBLIC LANDING PAGE"
      subtitle="Edit your public-facing landing page content, profile, gallery, and theme"
      actions={
        <div className="flex gap-2">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-ghost inline-flex items-center gap-2 text-xs"
          >
            <Eye size={14} />
            Preview
          </a>
          <button
            onClick={loadConfig}
            disabled={loading}
            className="btn-ghost inline-flex items-center gap-2 text-xs"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Reload
          </button>
          <button
            onClick={saveConfig}
            disabled={saving}
            className="btn-primary inline-flex items-center gap-2 text-xs"
          >
            <Save size={14} />
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      }
    >
      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b pb-2 overflow-x-auto" style={{ borderColor: "var(--gio-border)" }}>
        {[
          { key: TABS.BASICS, label: "Basics", icon: User },
          { key: TABS.PROFILE, label: "Profile", icon: Settings },
          { key: TABS.GALLERY, label: "Gallery", icon: ImageIcon },
          { key: TABS.LINKS, label: "Links", icon: LinkIcon },
          { key: TABS.SOCIALS, label: "Socials", icon: LinkIcon },
          { key: TABS.THEME, label: "Theme", icon: Palette },
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg inline-flex items-center gap-2 text-sm font-bold transition whitespace-nowrap ${
                activeTab === tab.key ? "" : "opacity-60 hover:opacity-100"
              }`}
              style={{
                background: activeTab === tab.key ? "var(--gio-surface)" : "transparent",
                color: activeTab === tab.key ? "var(--gio-text)" : "var(--gio-muted)",
                border: activeTab === tab.key ? "1px solid var(--gio-border)" : "1px solid transparent",
              }}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Basics Tab */}
      {activeTab === TABS.BASICS && (
        <div className="space-y-6">
          <Section title="Basic Information" icon={User}>
            <FormField label="Name" value={config.name} onChange={(v) => updateField("name", v)} />
            <FormField label="Tagline" value={config.tagline} onChange={(v) => updateField("tagline", v)} />
            <FormField label="Bio" value={config.bio} onChange={(v) => updateField("bio", v)} textarea rows={3} />
            <FormField label="About Section" value={config.about} onChange={(v) => updateField("about", v)} textarea rows={5} />
          </Section>

          <Section title="Images" icon={ImageIcon}>
            <FormField label="Avatar URL" value={config.avatar_url} onChange={(v) => updateField("avatar_url", v)} placeholder="https://..." />
            <FormField label="Cover URL" value={config.cover_url} onChange={(v) => updateField("cover_url", v)} placeholder="https://..." />
          </Section>

          <Section title="Call-to-Action Intros" icon={MessageCircle}>
            <FormField label="Message Form Intro" value={config.message_intro} onChange={(v) => updateField("message_intro", v)} textarea rows={2} />
          </Section>

          <Section title="Visibility Toggles" icon={Eye}>
            <Toggle label="Show Message Form" checked={config.show_message_form} onChange={(v) => updateField("show_message_form", v)} />
            <Toggle label="Show Model Apply Link" checked={config.show_model_apply} onChange={(v) => updateField("show_model_apply", v)} />
          </Section>

          <Section title="Member Areas Text" icon={User}>
            <FormField label="Free Area Title" value={config.free_area_title} onChange={(v) => updateField("free_area_title", v)} placeholder="Welcome to the Collection" />
            <FormField label="Free Area Description" value={config.free_area_description} onChange={(v) => updateField("free_area_description", v)} textarea rows={2} placeholder="Enjoy exclusive trailers..." />
          </Section>
        </div>
      )}

      {/* Profile Tab */}
      {activeTab === TABS.PROFILE && (
        <div className="space-y-6">
          <Section title="Physical Attributes" icon={User}>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Age" value={config.profile_attrs.age} onChange={(v) => updateProfileAttr("age", v)} />
              <FormField label="Ethnicity" value={config.profile_attrs.ethnicity} onChange={(v) => updateProfileAttr("ethnicity", v)} />
              <FormField label="Height" value={config.profile_attrs.height} onChange={(v) => updateProfileAttr("height", v)} placeholder="e.g., 180cm / 5'11" />
              <FormField label="Weight" value={config.profile_attrs.weight} onChange={(v) => updateProfileAttr("weight", v)} placeholder="e.g., 75kg / 165lbs" />
              <FormField label="Cock Size" value={config.profile_attrs.cock_size} onChange={(v) => updateProfileAttr("cock_size", v)} placeholder="e.g., 18cm / 7in" />
              <FormField label="Foreskin" value={config.profile_attrs.foreskin} onChange={(v) => updateProfileAttr("foreskin", v)} placeholder="cut / uncut" />
              <FormField label="Position" value={config.profile_attrs.position} onChange={(v) => updateProfileAttr("position", v)} placeholder="top / bottom / versatile" />
              <FormField label="Body Type" value={config.profile_attrs.body_type} onChange={(v) => updateProfileAttr("body_type", v)} />
              <FormField label="Body Hair" value={config.profile_attrs.body_hair} onChange={(v) => updateProfileAttr("body_hair", v)} />
              <FormField label="Eye Color" value={config.profile_attrs.eye_color} onChange={(v) => updateProfileAttr("eye_color", v)} />
              <FormField label="Hair Color" value={config.profile_attrs.hair_color} onChange={(v) => updateProfileAttr("hair_color", v)} />
              <FormField label="Orientation" value={config.profile_attrs.orientation} onChange={(v) => updateProfileAttr("orientation", v)} />
            </div>
          </Section>

          <Section title="Lifestyle & Details" icon={Settings}>
            <div className="grid md:grid-cols-2 gap-4">
              <FormField label="Style" value={config.profile_attrs.style} onChange={(v) => updateProfileAttr("style", v)} />
              <FormField label="Zodiac" value={config.profile_attrs.zodiac} onChange={(v) => updateProfileAttr("zodiac", v)} />
              <FormField label="Piercings" value={config.profile_attrs.piercings} onChange={(v) => updateProfileAttr("piercings", v)} />
              <FormField label="Tattoos" value={config.profile_attrs.tattoos} onChange={(v) => updateProfileAttr("tattoos", v)} />
              <FormField label="Smoking" value={config.profile_attrs.smoking} onChange={(v) => updateProfileAttr("smoking", v)} />
              <FormField label="Safe/BB" value={config.profile_attrs.safe_bb} onChange={(v) => updateProfileAttr("safe_bb", v)} placeholder="safe / bb / both" />
              <FormField label="Health Status" value={config.profile_attrs.health_status} onChange={(v) => updateProfileAttr("health_status", v)} />
              <FormField label="HIV Status" value={config.profile_attrs.hiv_status} onChange={(v) => updateProfileAttr("hiv_status", v)} />
            </div>
          </Section>
        </div>
      )}

      {/* Gallery Tab */}
      {activeTab === TABS.GALLERY && (
        <div className="space-y-6">
          <Section
            title="Gallery Photos"
            icon={ImageIcon}
            action={
              <button onClick={addGalleryPhoto} className="btn-ghost text-xs inline-flex items-center gap-1">
                <Plus size={14} />
                Add Photo
              </button>
            }
          >
            {config.gallery_photos.length === 0 ? (
              <div className="text-center py-12" style={{ color: "var(--gio-muted-3)" }}>
                <ImageIcon size={32} className="mx-auto mb-2" />
                <p className="text-sm">No gallery photos yet</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {config.gallery_photos.map((photo, i) => (
                  <div
                    key={i}
                    className="rounded-lg border overflow-hidden group relative"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}
                  >
                    <img src={photo.url} alt={`Gallery ${i + 1}`} className="w-full h-48 object-cover" />
                    <button
                      onClick={() => removeGalleryPhoto(i)}
                      className="absolute top-2 right-2 p-2 bg-red-500/90 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={14} />
                    </button>
                    <div className="p-3">
                      <input
                        type="text"
                        value={photo.caption}
                        onChange={(e) => {
                          const updated = [...config.gallery_photos];
                          updated[i].caption = e.target.value;
                          setConfig((prev) => ({ ...prev, gallery_photos: updated }));
                        }}
                        placeholder="Caption (optional)"
                        className="gio-input w-full text-xs"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Links Tab */}
      {activeTab === TABS.LINKS && (
        <Section
          title="Landing Page Links"
          icon={LinkIcon}
          action={(
            <button type="button" onClick={addLink} className="btn-primary text-xs inline-flex items-center gap-1">
              <Plus size={14} /> Add Link
            </button>
          )}
        >
          {config.links.length === 0 ? (
            <div className="text-center py-10" style={{ color: "var(--gio-muted-3)" }}>
              <LinkIcon size={30} className="mx-auto mb-2" />
              <p className="text-sm">No landing page links yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {config.links.map((link, index) => (
                <div
                  key={link.key || index}
                  className="grid gap-3 rounded-lg border p-4 md:grid-cols-[1fr_1fr_auto]"
                  style={{ borderColor: "var(--gio-border)", background: "var(--gio-bg)" }}
                >
                  <FormField
                    label="Link label"
                    value={link.label || ""}
                    onChange={(value) => updateLink(index, { label: value })}
                    placeholder="e.g. VIP Membership"
                  />
                  <FormField
                    label="URL"
                    value={link.url || ""}
                    onChange={(value) => updateLink(index, { url: value })}
                    placeholder="https://..."
                  />
                  <div className="flex items-end gap-1 pb-4">
                    <button
                      type="button"
                      onClick={() => moveLink(index, -1)}
                      disabled={index === 0}
                      aria-label={`Move link ${index + 1} up`}
                      className="p-2 disabled:opacity-30"
                      title="Move up"
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => moveLink(index, 1)}
                      disabled={index === config.links.length - 1}
                      aria-label={`Move link ${index + 1} down`}
                      className="p-2 disabled:opacity-30"
                      title="Move down"
                    >
                      <ChevronDown size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={() => updateLink(index, { enabled: !link.enabled })}
                      aria-label={`${link.enabled ? "Disable" : "Enable"} link ${index + 1}`}
                      className="p-2"
                      title={link.enabled ? "Disable link" : "Enable link"}
                    >
                      {link.enabled ? <Eye size={16} /> : <EyeOff size={16} />}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeLink(index)}
                      aria-label={`Delete link ${index + 1}`}
                      className="p-2 text-red-500"
                      title="Delete link"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="md:col-span-2">
                    <FormField
                      label="Subtitle (optional)"
                      value={link.sub || ""}
                      onChange={(value) => updateLink(index, { sub: value })}
                      placeholder="A short description"
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Socials Tab */}
      {activeTab === TABS.SOCIALS && (
        <div className="space-y-6">
          <Section
            title="Social Links"
            icon={LinkIcon}
            action={
              <button onClick={addSocial} className="btn-ghost text-xs inline-flex items-center gap-1">
                <Plus size={14} />
                Add Social
              </button>
            }
          >
            {config.socials.length === 0 ? (
              <div className="text-center py-12" style={{ color: "var(--gio-muted-3)" }}>
                <LinkIcon size={32} className="mx-auto mb-2" />
                <p className="text-sm">No social links yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {config.socials.map((social, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-4 rounded-lg border"
                    style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}
                  >
                    <SocialIcon platform={social.key} />
                    <div className="flex-1">
                      <div className="font-bold text-sm capitalize" style={{ color: "var(--gio-text)" }}>
                        {social.key}
                      </div>
                      <div className="text-xs font-mono truncate" style={{ color: "var(--gio-muted-2)" }}>
                        {social.url}
                      </div>
                    </div>
                    <button
                      onClick={() => removeSocial(i)}
                      className="p-2 hover:bg-red-500/10 rounded-lg transition-colors"
                    >
                      <Trash2 size={16} style={{ color: "#ef4444" }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      )}

      {/* Theme Tab */}
      {activeTab === TABS.THEME && (
        <div className="space-y-6">
          <Section title="Theme Selection" icon={Palette}>
            <div className="grid md:grid-cols-2 gap-4">
              {THEMES.map((theme) => (
                <button
                  key={theme.value}
                  onClick={() => updateField("theme", theme.value)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    config.theme === theme.value ? "border-[color:var(--gio-primary)]" : "border-transparent"
                  }`}
                  style={{
                    background: config.theme === theme.value ? "var(--gio-primary-soft)" : "var(--gio-surface)",
                  }}
                >
                  <div className="font-bold text-sm mb-1" style={{ color: "var(--gio-text)" }}>
                    {theme.label}
                  </div>
                  <div className="text-xs" style={{ color: "var(--gio-muted-2)" }}>
                    {theme.description}
                  </div>
                </button>
              ))}
            </div>
          </Section>

          <Section title="Hero Size" icon={Settings}>
            <div className="grid md:grid-cols-3 gap-4">
              {HERO_SIZES.map((size) => (
                <button
                  key={size.value}
                  onClick={() => updateField("hero_size", size.value)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    config.hero_size === size.value ? "border-[color:var(--gio-primary)]" : "border-transparent"
                  }`}
                  style={{
                    background: config.hero_size === size.value ? "var(--gio-primary-soft)" : "var(--gio-surface)",
                  }}
                >
                  <div className="font-bold text-sm mb-1" style={{ color: "var(--gio-text)" }}>
                    {size.label}
                  </div>
                  <div className="text-xs" style={{ color: "var(--gio-muted-2)" }}>
                    {size.description}
                  </div>
                </button>
              ))}
            </div>
          </Section>

          <Section title="Typography" icon={Settings}>
            <FormField label="Heading Font" value={config.font_heading} onChange={(v) => updateField("font_heading", v)} placeholder="Google Font name" />
            <FormField label="Body Font" value={config.font_body} onChange={(v) => updateField("font_body", v)} placeholder="Google Font name" />
          </Section>
        </div>
      )}
    </AppLayout>
  );
}

// Helper Components
function Section({ title, icon: Icon, children, action }) {
  return (
    <div
      className="rounded-lg border p-6"
      style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-lg flex items-center gap-2">
          {Icon && <Icon size={20} style={{ color: "var(--gio-primary)" }} />}
          <span style={{ color: "var(--gio-text)" }}>{title}</span>
        </h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function FormField({ label, value, onChange, textarea, rows = 4, placeholder = "" }) {
  return (
    <div className="mb-4">
      <label className="block text-sm font-bold mb-2" style={{ color: "var(--gio-text)" }}>
        {label}
      </label>
      {textarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder}
          className="gio-input w-full"
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="gio-input w-full"
        />
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: "var(--gio-bg)" }}>
      <span className="text-sm font-bold" style={{ color: "var(--gio-text)" }}>
        {label}
      </span>
      <button
        onClick={() => onChange(!checked)}
        className={`relative w-12 h-6 rounded-full transition-colors ${
          checked ? "bg-[color:var(--gio-primary)]" : "bg-[color:var(--gio-muted)]"
        }`}
      >
        <div
          className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
            checked ? "translate-x-7" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}

function SocialIcon({ platform }) {
  const icons = {
    twitter: <Twitter size={20} />,
    instagram: <Instagram size={20} />,
    email: <Mail size={20} />,
    telegram: <MessageCircle size={20} />,
  };
  return icons[platform] || <LinkIcon size={20} />;
}
